import { useMemo, useState } from 'react';
import { ArrowUpDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { downloadCsv, formatDays } from '@/lib/hr';
import { t } from '@/i18n';
import type { EmployeeStatsRow } from './hrStatsAggregation';

type SortKey = keyof Pick<
  EmployeeStatsRow,
  'employeeName' | 'department' | 'entitlementDays' | 'usedDays' | 'pendingDays'
  | 'remainingDays' | 'sickDays' | 'unavailabilityDays' | 'remoteDays' | 'absenceRatePct'
>;

const num = (v: number | null) => (v === null ? -1 : v);

/** Per-employee absence statistics: sortable columns + CSV export. */
export function EmployeeStatsTable({ rows, year }: { rows: EmployeeStatsRow[]; year: number }) {
  const [sortKey, setSortKey] = useState<SortKey>('employeeName');
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const cmp = typeof av === 'string' || typeof bv === 'string'
        ? String(av ?? '').localeCompare(String(bv ?? ''), 'pl')
        : num(av as number | null) - num(bv as number | null);
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(key === 'employeeName' || key === 'department');
    }
  };

  const exportCsv = () => {
    downloadCsv(`statystyki-pracownicy-${year}.csv`, [
      [
        t('hr.stats.colEmployee'), t('hr.stats.colDepartment'), t('hr.stats.colType'),
        t('hr.stats.colEntitlement'), t('hr.stats.colUsed'), t('hr.stats.colPending'),
        t('hr.stats.colRemaining'), t('hr.stats.colOnDemand'), t('hr.stats.colSick'),
        t('hr.stats.colUnavailability'), t('hr.stats.colRemote'), t('hr.stats.colRate'),
      ],
      ...sorted.map((r) => [
        r.employeeName, r.department,
        r.employmentType ? t(`employees.type.${r.employmentType}`) : '',
        r.entitlementDays, r.usedDays, r.pendingDays, r.remainingDays, r.onDemandUsedDays,
        r.sickDays, r.unavailabilityDays, r.remoteDays, r.absenceRatePct.toFixed(1),
      ]),
    ]);
  };

  const SortHead = ({ label, k, right }: { label: string; k: SortKey; right?: boolean }) => (
    <TableHead className={cn('whitespace-nowrap', right && 'text-right')}>
      <button
        type="button"
        className={cn('hover:text-foreground inline-flex items-center gap-1', sortKey === k && 'text-foreground')}
        onClick={() => toggleSort(k)}
      >
        {label}
        <ArrowUpDown className="size-3 opacity-50" />
      </button>
    </TableHead>
  );

  const pool = (v: number | null) => (v === null ? '—' : formatDays(v));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <CardTitle className="text-sm font-semibold">{t('hr.stats.tableTitle', { year })}</CardTitle>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="size-3.5" />
          {t('hr.stats.exportCsv')}
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto pt-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortHead label={t('hr.stats.colEmployee')} k="employeeName" />
              <SortHead label={t('hr.stats.colDepartment')} k="department" />
              <TableHead className="whitespace-nowrap">{t('hr.stats.colType')}</TableHead>
              <SortHead label={t('hr.stats.colEntitlement')} k="entitlementDays" right />
              <SortHead label={t('hr.stats.colUsed')} k="usedDays" right />
              <SortHead label={t('hr.stats.colPending')} k="pendingDays" right />
              <SortHead label={t('hr.stats.colRemaining')} k="remainingDays" right />
              <TableHead className="text-right whitespace-nowrap">{t('hr.stats.colOnDemand')}</TableHead>
              <SortHead label={t('hr.stats.colSick')} k="sickDays" right />
              <SortHead label={t('hr.stats.colUnavailability')} k="unavailabilityDays" right />
              <SortHead label={t('hr.stats.colRemote')} k="remoteDays" right />
              <SortHead label={t('hr.stats.colRate')} k="absenceRatePct" right />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-muted-foreground h-24 text-center">
                  {t('hr.stats.empty')}
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r) => (
                <TableRow key={r.employeeId}>
                  <TableCell className="py-2 font-medium whitespace-nowrap">{r.employeeName}</TableCell>
                  <TableCell className="text-muted-foreground py-2">{r.department ?? '—'}</TableCell>
                  <TableCell className="py-2 whitespace-nowrap">
                    {r.employmentType ? t(`employees.type.${r.employmentType}`) : '—'}
                  </TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{pool(r.entitlementDays)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{pool(r.usedDays)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{pool(r.pendingDays)}</TableCell>
                  <TableCell
                    className={cn(
                      'py-2 text-right font-medium tabular-nums',
                      r.remainingDays !== null && r.remainingDays < 0 && 'text-rose-600 dark:text-rose-400',
                    )}
                  >
                    {pool(r.remainingDays)}
                  </TableCell>
                  <TableCell className="py-2 text-right tabular-nums">
                    {r.onDemandUsedDays === null ? '—' : `${r.onDemandUsedDays}/4`}
                  </TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{formatDays(r.sickDays)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{formatDays(r.unavailabilityDays)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{formatDays(r.remoteDays)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{r.absenceRatePct.toFixed(1)}%</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
